import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { edgesState, equipmentTypesState, infoTypesState, nodesState } from '@/state';
import { useLoadableAtomValue, useUnwrappedAtomValue } from '@/state/loadableHooks';
import {
    type AnyGuideItem,
    type AppEdgeData,
    type AppFlowParams,
    type AppNodeData,
    type EquipmentTypeId,
    getGuideType,
    infoBindingForNode,
    type InfoTypeId,
} from '@rock/types';
import { assertUnreachable, filterNonNull } from '@rock/util';
import { groupBy, keyBy, orderBy } from 'lodash';
import type { FC } from 'react';
import { EquipmentTypeDisplay, InfoTypeDisplay } from '../nodes/NodeUI';

const TypesUsagePanel: FC<{ params: AppFlowParams }> = ({ params }) => {
    const spaceId = params.spaceId;
    const nodes = useUnwrappedAtomValue(nodesState(params)) ?? [];
    const edges = useUnwrappedAtomValue(edgesState(params)) ?? [];
    const [allEquipmentTypes, equipmentTypesLoadable] = useLoadableAtomValue(equipmentTypesState(spaceId), []);
    const [infoTypes, infoTypesLoadable] = useLoadableAtomValue(infoTypesState(spaceId), []);
    if (equipmentTypesLoadable.state !== 'hasData' || infoTypesLoadable.state !== 'hasData') {
        return null;
    }

    const allEquipmentTypesById = keyBy(allEquipmentTypes, (t) => t.id);

    // If we are working with an Equipment System, only show those Types that apply to it, otherwise all
    const guideType = getGuideType(params);
    const equipmentType = params.equipmentTypeId ? allEquipmentTypesById[params.equipmentTypeId] : undefined;
    const isEquipmentGuide = guideType === 'equipment';
    const isSystemScope = isEquipmentGuide && equipmentType?.scope === 'system';
    const systemScopedTypeIds = isSystemScope ? (equipmentType.systemParts?.map((t) => t.typeId) ?? []) : [];
    const equipmentTypesInScope = isSystemScope
        ? systemScopedTypeIds.map((id) => allEquipmentTypesById[id])
        : isEquipmentGuide
          ? [equipmentType]
          : allEquipmentTypes;
    const equipmentTypes = equipmentTypesInScope
        .filter(filterNonNull)
        .filter((type) => isSystemScope || type.scope === 'normal' || type.scope === 'system');
    const equipmentByGroup = groupBy(equipmentTypes, 'group');

    const usedEquipmentIds = new Set<EquipmentTypeId>();
    const usedInfoIds = new Set<InfoTypeId>();

    function countId(id: AnyGuideItem | undefined) {
        if (id) {
            switch (id.category) {
                case 'equipment': {
                    if (id.equipmentTypeId !== '[context]') {
                        usedEquipmentIds.add(id.equipmentTypeId);
                    }
                    break;
                }
                case 'info': {
                    usedInfoIds.add(id.infoTypeId);
                    break;
                }
                case 'consideration': {
                    // Ignore, since considerations are "wider" than guides
                    break;
                }
                default: {
                    assertUnreachable(id);
                }
            }
        }
    }

    for (const node of nodes) {
        const ids = getCreatedTypesForNode(node.data);
        for (const id of ids) {
            countId(id);
        }
    }
    for (const edge of edges) {
        if (edge.data) {
            const ids = getCreatedTypesForEdge(edge.data);
            for (const id of ids) {
                countId(id);
            }
        }
    }

    const usedEquipmentCount = equipmentTypes.filter((b) => usedEquipmentIds.has(b.id)).length;
    const usedInfoCount = infoTypes.filter((b) => usedInfoIds.has(b.id)).length;

    if (!equipmentTypes.length && !infoTypes.length) {
        return null;
    }

    return (
        <div className="flex flex-1 flex-col gap-2">
            <h4 className="text-md text-center">Types Used</h4>
            <Accordion type="multiple" className="px-3">
                {equipmentTypes.length > 0 && (
                    <AccordionItem value="equipment">
                        <AccordionTrigger className="cursor-pointer">
                            Equipment {usedEquipmentCount}/{equipmentTypes.length} &nbsp;
                            {`${usedEquipmentCount === equipmentTypes.length ? ' ✅' : ' ⚠️'}`}
                        </AccordionTrigger>
                        <AccordionContent>
                            {Object.keys(equipmentByGroup).map((group) => (
                                <div key={group} className="mb-2">
                                    <h6 className="text-xs font-semibold">{group}</h6>
                                    <div className="pl-2 flex flex-col gap-1">
                                        {orderBy(equipmentByGroup[group], 'order').map((type) => (
                                            <div key={type.id} className="flex items-center gap-2">
                                                <EquipmentTypeDisplay spaceId={spaceId} typeId={type.id} />
                                                <span>{usedEquipmentIds.has(type.id) ? '✅' : '⛔️'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </AccordionContent>
                    </AccordionItem>
                )}
                {infoTypes.length > 0 && (
                    <AccordionItem value="info">
                        <AccordionTrigger className="cursor-pointer">
                            Info {usedInfoCount}/{infoTypes.length} &nbsp;
                            {`${usedInfoCount === infoTypes.length ? ' ✅' : ' ⚠️'}`}
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="pl-2 flex flex-col gap-1">
                                {infoTypes.map((type) => (
                                    <div key={type.id} className="flex items-center gap-2">
                                        <InfoTypeDisplay spaceId={spaceId} typeId={type.id} />
                                        <span>{usedInfoIds.has(type.id) ? '✅' : '⛔️'}</span>
                                    </div>
                                ))}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                )}
            </Accordion>
        </div>
    );
};

export function getCreatedTypesForEdge(data: AppEdgeData): (AnyGuideItem | undefined)[] {
    switch (data.type) {
        case 'dataKey':
            return [...(data.items?.equipment ?? []), ...(data.items?.considerations ?? [])];
        default:
            assertUnreachable(data.type);
    }
}

function infoItemsForNode(data: AppNodeData): AnyGuideItem[] {
    const binding = infoBindingForNode(data);
    return binding ? [{ category: 'info', infoTypeId: binding.infoTypeId }] : [];
}

export function getCreatedTypesForNode(data: AppNodeData): (AnyGuideItem | undefined)[] {
    switch (data.type) {
        case 'address-lookup':
        case 'input':
        case 'yes-no':
            return infoItemsForNode(data);
        case 'create-equipment':
            return data.equipmentTypeId
                ? ([{ category: 'equipment', equipmentTypeId: data.equipmentTypeId }] as const)
                : [];
        case 'entry':
            return [];
        case 'equipment':
            return [
                // We do not return this, because this node doesn't actually create the equipment
                // data.equipmentBlockTypeId
            ];
        case 'equipment-attribute':
            return [];
        case 'equipment-list':
            return [];
        case 'exit':
            return [];
        case 'if':
            return [];
        case 'select': {
            const usedIds = new Set<AnyGuideItem>(infoItemsForNode(data));
            for (const section of data.sections) {
                for (const option of section.options) {
                    if (option.equipmentTypeId)
                        usedIds.add({ category: 'equipment', equipmentTypeId: option.equipmentTypeId });
                }
            }
            return [...usedIds];
        }
        case 'image':
            return [
                // We do not return this, because this node doesn't actually create the equipment
                // data.equipmentBlockTypeId
            ];
        case 'info':
            return [];
        case 'initial-rooms':
            return [];
        case 'return':
            return [];
        case 'room':
            return [];
        case 'room-starter-selection':
            return [];
        case 'scan':
            return [
                // We do not return this, because this node doesn't actually create the equipment
                // data.equipmentTypeId
            ];
        case 'set':
            return [];
        case 'sub-guide':
            return data.equipmentTypeId ? [{ category: 'equipment', equipmentTypeId: data.equipmentTypeId }] : [];
        default:
            assertUnreachable(data);
    }
}

export default TypesUsagePanel;
